import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface WeeklyScheduleProps {
  familyId: string;
  dependentIds?: string[];
}

const CATEGORY_COLORS: Record<string, string> = {
  SPORT: 'border-l-green-500',
  MUSIC: 'border-l-purple-500',
  ACADEMIC: 'border-l-blue-500',
  SOCIAL: 'border-l-pink-500',
  MEDICAL: 'border-l-red-500',
  TRANSPORT: 'border-l-amber-500',
  OTHER: 'border-l-gray-500',
};

interface WeekDay {
  day: string;
  sessions: Array<{
    activityId: string;
    activityName: string;
    category: string;
    venue?: string;
    dependentName: string;
    startTime: string;
    endTime: string;
    responsibleId?: string;
    responsibleName?: string;
  }>;
}

const DAY_LABELS: Record<string, string> = {
  MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed',
  THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun',
};

export function WeeklySchedule({ familyId, dependentIds }: WeeklyScheduleProps) {
  const [schedule, setSchedule] = useState<WeekDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSchedule();
  }, [familyId, dependentIds?.join(',')]);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const dependentId = dependentIds?.length === 1 ? dependentIds[0] : undefined;
      const data = await api.getWeekSchedule(familyId, dependentId);
      setSchedule(data);
    } catch {
      setSchedule([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500 text-sm">Loading schedule...</div>;
  }

  const hasSessions = schedule.some((d) => d.sessions.length > 0);

  if (!hasSessions) {
    return <div className="text-gray-500 text-sm">No activities scheduled this week.</div>;
  }

  return (
    <div className="grid grid-cols-7 gap-1">
      {schedule.map((day) => (
        <div key={day.day} className="min-h-[80px]">
          <div className="text-xs font-semibold text-gray-500 text-center py-1 border-b border-gray-200">
            {DAY_LABELS[day.day] || day.day}
          </div>
          <div className="space-y-1 mt-1">
            {day.sessions.map((session, i) => (
              <div
                key={`${session.activityId}-${i}`}
                className={`p-1.5 text-xs rounded border-l-2 bg-gray-50 ${CATEGORY_COLORS[session.category] || CATEGORY_COLORS.OTHER}`}
                title={`${session.activityName} (${session.startTime}-${session.endTime})${session.venue ? ` @ ${session.venue}` : ''}`}
              >
                <div className="font-medium text-gray-900 truncate">{session.activityName}</div>
                <div className="text-gray-500">{session.startTime}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
